/**
 * Multi-country currency helpers.
 *
 * Single source of truth for the presentation layer. Every screen that shows
 * money should call `useCurrency(centerCode)` (see hooks/useCurrency.js) and
 * pass the returned meta object to `formatMoney` — NEVER hard-code ₹ or $.
 *
 * The list of supported currencies mirrors backend/utils/currency.py.
 */

const CURRENCY_META = {
  INR: { symbol: '₹',  locale: 'en-IN' },
  AUD: { symbol: 'A$', locale: 'en-AU' },
  USD: { symbol: '$',  locale: 'en-US' },
  GBP: { symbol: '£',  locale: 'en-GB' },
  JPY: { symbol: '¥',  locale: 'ja-JP' },
  EUR: { symbol: '€',  locale: 'en-IE' },
  CAD: { symbol: 'C$', locale: 'en-CA' },
  SGD: { symbol: 'S$', locale: 'en-SG' },
  NZD: { symbol: 'NZ$', locale: 'en-NZ' },
};

const COUNTRY_TO_CODE = {
  india: 'INR', in: 'INR',
  australia: 'AUD', au: 'AUD', perth: 'AUD',
  usa: 'USD', 'united states': 'USD', us: 'USD',
  uk: 'GBP', 'united kingdom': 'GBP', gb: 'GBP', england: 'GBP',
  japan: 'JPY', jp: 'JPY',
  canada: 'CAD', ca: 'CAD',
  singapore: 'SGD', sg: 'SGD',
  'new zealand': 'NZD', nz: 'NZD',
  ireland: 'EUR', germany: 'EUR', france: 'EUR', spain: 'EUR', italy: 'EUR',
};

/**
 * Resolve currency meta from an ISO code (e.g. 'AUD') or from a center-shape
 * object with `{currency, country, is_india_center}` fields.
 * Falls back to INR — never returns null so downstream `.symbol` access is safe.
 */
export function resolveCurrency(input) {
  if (!input) return { code: 'INR', ...CURRENCY_META.INR };
  if (typeof input === 'string') {
    const code = input.toUpperCase();
    if (CURRENCY_META[code]) return { code, ...CURRENCY_META[code] };
    // maybe a country name was passed
    const mapped = COUNTRY_TO_CODE[input.toLowerCase()];
    if (mapped) return { code: mapped, ...CURRENCY_META[mapped] };
    return { code: 'INR', ...CURRENCY_META.INR };
  }
  const explicit = (input.currency || '').toUpperCase();
  if (CURRENCY_META[explicit]) return { code: explicit, ...CURRENCY_META[explicit] };
  const country = (input.country || '').toLowerCase();
  if (COUNTRY_TO_CODE[country]) {
    const c = COUNTRY_TO_CODE[country];
    return { code: c, ...CURRENCY_META[c] };
  }
  if (input.is_india_center === true) return { code: 'INR', ...CURRENCY_META.INR };
  return { code: 'INR', ...CURRENCY_META.INR };
}

/**
 * Format a number as money using the given `meta` from resolveCurrency().
 * Options: `{ digits=0, sign=false, hideSymbol=false }`.
 * A `null` / `NaN` value renders as "—" (single em dash) — matches the pattern
 * used elsewhere in the codebase.
 */
export function formatMoney(value, meta, opts = {}) {
  const { digits = 0, sign = false, hideSymbol = false, dash = '—' } = opts;
  const m = meta && meta.symbol !== undefined ? meta : resolveCurrency(meta);
  if (value == null || Number.isNaN(Number(value))) return dash;
  const n = Number(value);
  const sym = hideSymbol ? '' : m.symbol;
  const prefix = sign && n > 0 ? '+' : '';
  return `${prefix}${sym}${n.toLocaleString(m.locale, { maximumFractionDigits: digits, minimumFractionDigits: digits })}`;
}

/** Convenience — the raw symbol only. */
export function getCurrencySymbol(input) {
  return resolveCurrency(input).symbol;
}

/** Build a `fmt(value, digits=0)` closure — useful when a component needs to
 *  pass a formatter to many child renderers without repeating the meta. */
export function makeFormatter(meta) {
  const m = meta && meta.symbol !== undefined ? meta : resolveCurrency(meta);
  return (v, d = 0, sign = false) => formatMoney(v, m, { digits: d, sign });
}

export const CURRENCY_META_MAP = CURRENCY_META;
